/* eslint-disable react/display-name */
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { SearchOutlined } from '@ant-design/icons'
import { Col, Row, Space, Input } from 'antd'
import { connect, useDispatch } from 'react-redux'
import { RootState } from 'Store'
import { selectCurrentUser } from 'Store/user/selector'
import { numberWithComma, isFilter, formatDateWithoutTime, getSupplierOrderStatus } from 'helpers/format'
import { StatusTag } from 'Components/utilities'
import { TableListPage } from 'Components/common/Table'
import { PrimaryButton } from 'Components/button'
import Detail from './SupplierCreateTax'
import { Pagination } from 'Components/Pagination'
import Theme, { MyTheme } from 'Components/common/Theme'
import LinkText from 'Components/text/LinkText'
import { cloneDeep } from 'lodash'
import { quotationOrderListAPI } from 'Store/supplier-order/api'
import { SupplierOrderListData, SupplierOrderListReq } from 'Store/supplier-order/types'
import { selectSupplierOrderList, selectSupplierOrderListEvent } from 'Store/supplier-order/selector'

type Props = {} & ReduxProps

const SupplierOrders: React.FunctionComponent<Props> = props => {
  const { currentUser, listEvent, dataList } = props
  const dispatch = useDispatch()
  const [drawer, setDrawer] = useState(false)
  const [resetFilter, setResetFilter] = useState(false)
  const [selectedOrder, setSelected] = useState(undefined as any)

  const initFilter = {
    keyword: '',
    payment_type: '',
    delivery_type: '',
    start_date: '',
    end_date: '',
  }

  const [filter, setFilter] = useState(cloneDeep(initFilter))
  const [pagination, setPagination] = useState({
    Page: 1,
    RowPerPage: 10,
  })
  const meta = dataList.meta
  const datas = dataList.data

  const onGetDataList = (): void => {
    const req: SupplierOrderListReq = {
      keyword: filter.keyword,
      page: pagination.Page,
      per_page: pagination.RowPerPage,
      is_invoice: true,
      order_by: 'sale_order_id',
      sort: 'desc',
    }
    dispatch(quotationOrderListAPI(req, currentUser.accessToken, currentUser?.activeMerchant?.uuid || ''))
  }

  useEffect(() => {
    if (currentUser?.accessToken) {
      onGetDataList()
    }
  }, [pagination])

  useEffect(() => {
    if (resetFilter) {
      pagination.Page = 1
      setPagination({ ...pagination })
      setResetFilter(false)
    }
  }, [resetFilter])

  const onClickSearch = () => {
    pagination.Page = 1
    setPagination({ ...pagination })
  }

  useEffect(() => {
    if (pagination.Page === 1) return
    pagination.Page = 1
    pagination.RowPerPage = 10
    setPagination({ ...pagination })
  }, [])

  const onFilter = (key: string, val: string) => {
    setFilter({
      ...filter,
      [key]: val,
    })
  }

  const onResetFilter = () => {
    setFilter(cloneDeep({ ...initFilter }))
    setResetFilter(true)
  }

  const onOpenDrawer = () => {
    setDrawer(true)
  }

  const onCloseDrawer = () => {
    setDrawer(false)
  }

  const onClickEdit = (order: any) => {
    setSelected(order)
    onOpenDrawer()
  }

  const onSuccess = () => {
    onCloseDrawer()
    onGetDataList()
  }

  const renderStatus = (status: string) => {
    const result = getSupplierOrderStatus(status)
    return <StatusTag color={result.color} text={result.text} />
  }

  const renderActionButtion = (status: string, order: SupplierOrderListData) => {
    return <PrimaryButton text={'ออกใบกำกับภาษี'} size="middle" onClick={() => onClickEdit(order)} />
  }

  const columns = [
    {
      title: 'ลำดับ',
      dataIndex: 'no',
      key: 'no',
      render: (value: any, record: any, index: number) => {
        return index + 1 + (pagination.Page - 1) * pagination.RowPerPage
      },
      width: '60px',
    },
    {
      title: 'เลขที่สั่งซื้อ',
      dataIndex: 'sale_order_no',
      key: 'sale_order_no',
      width: '180px',
    },
    {
      title: 'เลขที่ใบเสนอราคา',
      dataIndex: 'quotation_no',
      key: 'quotation_no',
      width: '180px',
    },
    {
      title: 'ชื่อลูกค้า',
      dataIndex: 'customer_name',
      key: 'customer_name',
    },
    {
      title: <TextRight>จำนวน/รายการ</TextRight>,
      dataIndex: 'total_quantity',
      key: 'total_quantity',
      render: (val: number) => {
        return <TextRight>{numberWithComma(val)}</TextRight>
      },
    },
    {
      title: <TextRight>ราคารวม</TextRight>,
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (val: number) => {
        return <TextRight>{numberWithComma(val, true)}</TextRight>
      },
    },
    {
      title: 'วันที่จัดส่ง',
      dataIndex: 'delivered_dt',
      key: 'delivered_dt',
      render: (val: string) => formatDateWithoutTime(val || '') || '-',
    },
    {
      title: 'สถานะ',
      key: 'status',
      dataIndex: 'status',
      render: renderStatus,
    },
    {
      title: 'ออกใบกำกับภาษี',
      key: 'action',
      dataIndex: 'action',
      width: '150px',
      render: renderActionButtion,
    },
  ]

  const DetailDrawer = (
    <Detail
      orderNo={selectedOrder?.sale_order_no}
      id={selectedOrder?.sale_order_id}
      visible={drawer}
      onCloseDrawer={onCloseDrawer}
      onSuccess={onSuccess}
    />
  )

  const FilterContent = (
    <FilterWrapper>
      <Title>ค้นหาหมายเลขคำสั่งซื้อเพื่อออกใบกำกับภาษีเต็มรูปแบบ</Title>
      <Row justify="center" align="bottom" gutter={[8, 1]}>
        <MyCol xs={24} md={12}>
          <SearchInput
            placeholder="ระบุหมายเลขคำสั่งซื้อ"
            prefix={<SearchOutlined />}
            allowClear
            value={filter.keyword}
            onChange={(evt: any): void => onFilter('keyword', evt.target.value)}
          />
        </MyCol>
        <MyCol>
          <Space size="middle">
            <PrimaryButton text="ค้นหา" btnType="Cancel" onClick={onClickSearch} />
            {isFilter(filter) && (
              <LinkText color={Theme.colors.primary} onClick={onResetFilter}>
                ล้างการค้นหา
              </LinkText>
            )}
          </Space>
        </MyCol>
      </Row>
    </FilterWrapper>
  )

  const PaginationContent = (
    <Pagination
      currentPage={pagination.Page}
      totalRows={meta?.totalCount || 0}
      perPage={pagination.RowPerPage}
      onClickPage={(page: number) => {
        pagination.Page = page
        setPagination({ ...pagination })
      }}
      onChangeNumber={(number: number) => {
        pagination.RowPerPage = number
        setPagination({ ...pagination })
      }}
    />
  )

  return (
    <>
      {FilterContent}
      <TableListPage
        columns={columns}
        datas={datas}
        isLoading={listEvent.IsLoading}
        paginationContent={PaginationContent}
        size="middle"
        offsetHeader={25}
      />
      {DetailDrawer}
    </>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    currentUser: selectCurrentUser(state),
    dataList: selectSupplierOrderList(state),
    listEvent: selectSupplierOrderListEvent(state),
  }
}

type ReduxProps = ReturnType<typeof mapStateToProps>

const FilterWrapper = styled.div`
  padding: 30px 10px;
  border: 1px solid #c4c4c4;
  box-sizing: border-box;
  border-radius: 10px;
  text-align: center;
`

const MyCol = styled(Col)`
  margin-top: 10px;
`

const SearchInput = styled(Input)`
  border-radius: 6px;
  height: 45px;
`

const Title = styled.div`
  font-size: ${(props: { theme: MyTheme }): string => props.theme.typography.body3};
`

const TextRight = styled.div`
  text-align: right;
  margin-right: 20px;
`
export default connect(mapStateToProps)(SupplierOrders)
